// Builds the Console's examples and guided lessons from what GET /v1/examples
// returns for the environment. The templates here know no hub, intent, or
// question: every question and ask comes from the response, so a deployment
// teaches with the hubs it lists. A lesson whose questions the response
// doesn't hold is left out. See Design > Console, "Lessons".

import type { Lesson, Step } from "./lesson.ts";
import { validateRequest } from "./request.ts";
import type { ExamplesResponse } from "./types.ts";

export type UseCase = { label: string; text: string; maxCostPerAnswer: number };

const MAX_USE_CASES = 6;
const BID_STEP = 100;

/** A bid well above an ask, in a round number of US dollars per million answers. */
export const bidAbove = (ask: number): number => Math.max(BID_STEP, Math.ceil((ask * 2) / BID_STEP) * BID_STEP);

/** A bid below an ask, so the hub won't answer. */
export const bidBelow = (ask: number): number => Math.max(1, Math.floor(ask / 2));

const fits = (text: string, bid: number) => validateRequest({ questions: [{ text, maxCostPerAnswer: bid }] }).length === 0;

type Question = { question: string; intent: string | null; hub: string };

// Words that say nothing about what a question is about, left out when comparing two questions' subjects.
const FILLER = new Set(["what", "which", "who", "whom", "when", "where", "why", "how", "many", "much", "does", "did", "do", "is", "are", "was", "were",
  "will", "be", "the", "a", "an", "of", "in", "on", "at", "to", "for", "by", "and", "or", "as", "it", "its", "this", "that", "have", "has"]);
const subjectWords = (q: string) => new Set(q.toLowerCase().replace(/'s\b/g, "").split(/[^a-z0-9]+/).filter((w) => w.length > 1 && !FILLER.has(w)));
/** How many subject words two questions share, such as "Raleigh, North Carolina". */
const shared = (a: string, b: string) => { const words = subjectWords(b); return [...subjectWords(a)].filter((w) => words.has(w)).length; };

export type Content = { lessons: Lesson[]; useCases: UseCase[] };

export function buildContent(examples: ExamplesResponse): Content {
  const intents = examples.hubs.flatMap((h) => h.intents);
  const asks = intents.map((i) => i.askUsdPerMillion).filter((a) => a > 0);
  if (asks.length === 0) return { lessons: [], useCases: [] };
  const high = bidAbove(Math.max(...asks));
  const low = bidBelow(Math.min(...asks));

  // One example per intent, taking turns between hubs so one hub doesn't fill the list.
  const perHub = examples.hubs.map((h) => h.intents.flatMap((i) => (i.examples[0] && fits(i.examples[0], bidAbove(i.askUsdPerMillion))
    ? [{ label: i.label, text: i.examples[0], maxCostPerAnswer: bidAbove(i.askUsdPerMillion) }] : [])));
  const useCases: UseCase[] = [];
  for (let round = 0; useCases.length < MAX_USE_CASES && perHub.some((list) => round < list.length); round++) {
    for (const list of perHub) {
      const useCase = list[round];
      if (useCase && useCases.length < MAX_USE_CASES) useCases.push(useCase);
    }
  }

  const questions = (outcome: string): Question[] => examples.hubs.flatMap((h) =>
    h.questions.filter((q) => q.outcome === outcome && fits(q.question, high)).map((q) => ({ question: q.question, intent: q.intent, hub: h.hub })));
  const answers = questions("Answer");
  const missing = questions("MissingParameters");
  const declined = questions("OutOfScope");

  const request = (text: string, bid: number) => ({ questions: [{ id: "q1", text, maxCostPerAnswer: bid }] });
  const edit = (label: string, coach: string, chip: { label: string; set: Extract<Step, { kind: "edit" }>["chip"]["set"] }): Step => ({ kind: "edit", label, coach, chip, gateSend: true });
  const send = (label: string, coach: string, ...expect: Extract<Step, { kind: "send" }>["expect"]): Step => ({ kind: "send", label, coach, expect });

  const drafts: Omit<Lesson, "number" | "next">[] = [];
  const first = answers[0];
  if (first) drafts.push({
    id: "first", title: "Your first answer", eyebrow: "FIRST ANSWER", blurb: first.question, badge: "Answer", illustration: "answer", start: null,
    steps: [
      { kind: "pick", label: "Start with a question", coach: "A request is a question and a bid, written as JSON. Start by choosing Question.", text: first.question },
      edit("Set a bid", "A bid is the most you'll pay for an answer, in US dollars per million answers. The request needs one before it can be sent.",
        { label: `Add a bid, $${high} per million`, set: { maxCostPerAnswer: high } }),
      send("Send the request", "The request is ready: one question and a bid. Send it.", "Answer"),
    ],
  });
  const priced = answers[1] ?? first;
  if (priced) drafts.push({
    id: "bid", title: "Bid what it's worth", eyebrow: "PRICES", blurb: "You pay the ask, never more than your bid", badge: "PriceTooLow", illustration: "bid",
    start: request(priced.question, low),
    steps: [
      send("Send the request", `A bid is the most you'll pay per million answers. Send this request as it is, with a bid of $${low} per million, and see which hubs answer.`, "PriceTooLow"),
      edit("Raise your bid to the ask", "A hub knows the answer, but its ask is above your bid, so it didn't answer and you weren't charged. Raise your bid to at least the ask.",
        { label: "Raise to the ask", set: { maxCostPerAnswer: "ask" } }),
      send("Send again", "The bid now covers the ask. You pay the ask, never more than your bid. Send the request again.", "Answer"),
    ],
  });
  // A missing-detail question, and a complete question for the same intent to send in its place.
  const gap = missing.map((m) => ({ m, fix: answers.find((a) => a.intent === m.intent && a.hub === m.hub) })).find((x) => x.fix);
  if (gap?.fix) drafts.push({
    id: "missing", title: "Fill in the missing detail", eyebrow: "MISSING DETAIL", blurb: "When a hub needs one more fact", badge: "MissingParameters", illustration: "missing",
    start: request(gap.m.question, high),
    steps: [
      send("Send the request", "A hub can only answer when it knows what you're asking about. Send this question as it is, and see what the hub says.", "MissingParameters"),
      edit("Add the missing detail", "The hub read your question, but it's missing a detail. The hub tells you what's missing, and you weren't charged. Add it.",
        { label: "Add the missing detail", set: { text: gap.fix.question } }),
      send("Send again", "The question now has what the hub asked for. Send it again.", "Answer"),
    ],
  });
  // One hub's story (#184): a question about something the hub knows, asking for what it doesn't hold, then a question
  // the same hub answers about the same subject. The pair is the declined and answered questions of one hub that share
  // the most subject words ("Raleigh, North Carolina"); a declined question far from every answer, such as one about
  // the Super Bowl, is never chosen while a closer pair exists. A hub that answers nothing (no built intents) is passed over.
  const pairs = declined.flatMap((out) => answers.filter((a) => a.hub === out.hub).map((covered) => ({ out, covered, score: shared(out.question, covered.question) })));
  const scope = pairs.reduce<(typeof pairs)[number] | undefined>((best, p) => (!best || p.score > best.score ? p : best), undefined);
  const out = scope?.out;
  const covered = scope?.covered;
  const holds = out && examples.hubs.find((h) => h.hub === out.hub)?.scope;
  if (out && covered) {
    const hub = out.hub.split("/").pop() ?? out.hub;
    drafts.push({
      id: "scope", title: "When a hub can't answer", eyebrow: "OUT OF SCOPE", blurb: out.question, badge: "OutOfScope", illustration: "scope",
      start: request(out.question, high),
      steps: [
        send("Send the question", `This question is about something ${hub} knows, but asks for something it may not hold. Send it and see what the hub says.`, "OutOfScope"),
        edit("Ask what it does hold", `${hub} declined, and you weren't charged. Its reason says what it doesn't hold.${holds ? ` What it does hold: ${holds}` : ""} Ask it something it holds about the same subject.`,
          { label: "Ask something it holds", set: { text: covered.question } }),
        send("Send the new question", `Same subject, something ${hub} holds. Send it.`, "Answer"),
      ],
    });
  }
  const wrong = answers[2] ?? first;
  if (wrong) drafts.push({
    id: "refund", title: "Reject a wrong answer", eyebrow: "REFUNDS", blurb: "Send it back and get a refund", badge: "Answer", illustration: "refund",
    start: request(wrong.question, high),
    steps: [
      send("Read the answer", "You pay for an answer. If it isn't right, you can send it back and get your money back. Send this request and read the answer.", "Answer"),
      { kind: "reject", label: "Reject it", coach: "If the answer isn't right, reject it. Say why if you like, and you're credited the full price at once." },
      { kind: "credits", label: "Check your credits", coach: "The refund is in your credits, and it counts against the hub's rating. Open your balance to see it." },
    ],
  });

  const lessons = drafts.map((d, i) => ({ ...d, number: i + 1, next: drafts[i + 1]?.id ?? null }));
  return { lessons, useCases };
}
